export class UtilHelper {
    /**
     * Check if variable is object
     */
    static isObject(item: any): boolean {
        return typeof item === "object" && item !== null
    }

    /**
     * Check if object is empty
     */
    static isEmptyObject(item: any): boolean {
        if (!UtilHelper.isObject(item) || item.constructor !== Object) {
            return false
        }

        const keys = Object.keys(item)

        if (!keys.length) {
            return true
        }

        return keys.every(key => item[key] === undefined)
    }

    /**
     * Format cents to dollars
     */
    static formatCents(amount: string | number): number {
        return parseFloat((parseInt(amount.toString()) / 100).toFixed(2))
    }

    /**
     * Format dollars to cents
     */
    static formatDollarsToCents(amount: string | number): number {
        return Math.round(parseFloat(amount.toString()) * 100)
    }

    /**
     * Get formatted money with currency sign
     * Should come in cents
     */
    static getFormattedMoney(
        amount: number,
        showZeroFractionDigits = false,
        currency = "USD",
        forceFractionDigits = false,
    ): string {
        const result = UtilHelper.formatCents(amount).toLocaleString("en-US", {
            style: "currency",
            currency,
        })

        if (showZeroFractionDigits && !forceFractionDigits) {
            return result.split(".")[0]
        }

        return result
    }

    /**
     * Generate unique varchar identifier
     */
    static generateUniqueVarchar(length = 16, withTimestamp = true): string {
        let result = ""
        const characters =
            "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"

        for (let i = 0; i < length; i++) {
            result += characters.charAt(
                Math.floor(Math.random() * characters.length),
            )
        }

        return withTimestamp ? `${result}${new Date().getTime()}` : result
    }

    /**
     * Sleep for X seconds
     */
    static async sleep(seconds = 1): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, seconds * 1000))
    }

    /**
     * Convert camelCase to snake_case
     */
    static camelCaseToSnakeCase(str: string): string {
        return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
    }

    /**
     * Convert snake_case to camelCase
     */
    static snakeCaseToCamelCase(str: string): string {
        return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    }

    /**
     * Convert camelCase to snake_case in object keys
     */
    static objectCamelCaseToSnakeCase(
        item: Record<string, any>,
    ): Record<string, any> {
        return Object.keys(item).reduce((memo, key) => {
            memo[UtilHelper.camelCaseToSnakeCase(key)] = item[key]

            return memo
        }, {} as Record<string, any>)
    }

    /**
     * Convert snake_case to camelCase in object keys
     */
    static objectSnakeCaseToCamelCase(
        item: Record<string, any>,
    ): Record<string, any> {
        return Object.keys(item).reduce((memo, key) => {
            memo[UtilHelper.snakeCaseToCamelCase(key)] = item[key]

            return memo
        }, {} as Record<string, any>)
    }
}
